import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import AdminHeader from "../components/Layout/AdminHeader";
import AdminSideBar from "../components/Admin/Layout/AdminSideBar";
import { SimpleLoader } from "../components/Layout/Loader";
import { server } from "../server";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(value) || 0);

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB");
};

const statusBadge = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "open" || key === "mediating") return "bg-amber-100 text-amber-800";
  if (key === "resolved") return "bg-emerald-100 text-emerald-800";
  return "bg-slate-100 text-slate-700";
};

const outcomeLabel = (type) => {
  const labels = {
    mediation: "Mediation / guidance",
    full_refund: "Full refund",
    partial_refund: "Partial refund",
    case_closed: "Case closed",
    return_required: "Return required",
  };
  return labels[type] || type || "—";
};

const AdminDashboardVaMediation = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get("status") || "open";
  const [statusFilter, setStatusFilter] = useState(
    ["open", "resolved", "all"].includes(initialStatus) ? initialStatus : "open"
  );
  const [cases, setCases] = useState([]);
  const [counts, setCounts] = useState({ open: 0, resolved: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const fetchIdRef = useRef(0);

  const highlightedOrderId = searchParams.get("order");

  const fetchCases = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await axios.get(`${server}/order/admin-mediation-cases`, {
        params: { status: statusFilter },
        withCredentials: true,
      });
      if (fetchId !== fetchIdRef.current) return;
      setCases(Array.isArray(data?.cases) ? data.cases : []);
      setCounts(data?.counts || { open: 0, resolved: 0, all: 0 });
    } catch (error) {
      if (fetchId !== fetchIdRef.current) return;
      const message =
        error?.response?.data?.message || "Failed to load VA mediation cases";
      setLoadError(message);
      setCases([]);
      toast.error(message);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  useEffect(() => {
    if (!highlightedOrderId || loading || !cases.length) return;
    const el = document.getElementById(`mediation-case-${highlightedOrderId}`);
    if (!el) return;
    const timer = window.setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [highlightedOrderId, cases, loading]);

  const filteredCases = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter((item) => {
      const haystack = [
        item.orderDisplayId,
        item.orderNumber,
        item.buyerName,
        item.sellerName,
        item.refundReason,
        item.vaMediation?.buyerNote,
        item.vaMediation?.outcomeSummary,
        item.vaMediation?.outcomeType,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [cases, searchQuery]);

  const selectStatus = (status) => {
    setStatusFilter(status);
    const next = new URLSearchParams(searchParams);
    if (status === "open") next.delete("status");
    else next.set("status", status);
    setSearchParams(next, { replace: true });
  };

  const openCase = (orderId) => {
    navigate(`/admin-order/${orderId}`);
  };

  const emptyMessage =
    statusFilter === "open"
      ? "No open VA mediation requests."
      : statusFilter === "resolved"
        ? "No resolved VA mediation cases."
        : "No mediation cases match this filter.";

  return (
    <div className="min-h-screen">
      <AdminHeader />
      <div className="mx-auto flex max-w-[1500px] flex-col gap-6 px-4 py-6 lg:flex-row lg:px-6">
        <div className="w-full lg:w-[300px]">
          <AdminSideBar active={17} />
        </div>
        <div className="flex-1">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="border-b border-slate-200 pb-6">
              <h1 className="text-2xl font-bold text-slate-900">VA Mediation</h1>
              <p className="mt-2 max-w-3xl text-sm text-slate-600">
                Manage buyer requests for Veteran Airsoft to step in on order disputes. Review the buyer–seller
                thread, then record an outcome from the order detail page.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              {[
                { id: "open", label: "Open", count: counts.open },
                { id: "resolved", label: "Resolved", count: counts.resolved },
                { id: "all", label: "All", count: counts.all },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => selectStatus(tab.id)}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                    statusFilter === tab.id
                      ? "bg-[#38513b] text-white shadow-sm"
                      : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {tab.label}
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-xs ${
                      statusFilter === tab.id ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-4">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by order, buyer, seller, or note…"
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
              />
            </div>

            {loading ? (
              <SimpleLoader />
            ) : loadError ? (
              <div className="mt-8 rounded-xl border border-rose-200 bg-rose-50 px-6 py-10 text-center">
                <p className="text-sm font-medium text-rose-800">{loadError}</p>
                <button
                  type="button"
                  onClick={fetchCases}
                  className="mt-4 rounded-lg bg-[#38513b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2f4232]"
                >
                  Try again
                </button>
              </div>
            ) : filteredCases.length === 0 ? (
              <div className="mt-8 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
                <p className="text-sm font-medium text-slate-700">{emptyMessage}</p>
                {statusFilter === "resolved" && counts.resolved > 0 ? (
                  <p className="mt-2 text-xs text-amber-800">
                    Badge shows {counts.resolved} resolved, but none loaded. Tap Try again or check All.
                  </p>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">
                    Buyers can ask VA to step in from their order page after 3 days with an active refund dispute.
                  </p>
                )}
                <button
                  type="button"
                  onClick={fetchCases}
                  className="mt-4 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Refresh
                </button>
              </div>
            ) : (
              <div className="mt-6 overflow-x-auto">
                <table className="min-w-[880px] w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-800 text-left text-xs font-bold uppercase tracking-wide text-white">
                      <th className="px-3 py-3">Order</th>
                      <th className="px-3 py-3">Status</th>
                      <th className="px-3 py-3">Buyer</th>
                      <th className="px-3 py-3">Seller</th>
                      <th className="px-3 py-3">Requested</th>
                      <th className="px-3 py-3">Buyer note</th>
                      <th className="px-3 py-3">Outcome</th>
                      <th className="px-3 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCases.map((item) => {
                      const vm = item.vaMediation || {};
                      const isHighlighted = highlightedOrderId === String(item._id);
                      return (
                        <tr
                          key={String(item._id)}
                          id={`mediation-case-${item._id}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => openCase(item._id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openCase(item._id);
                            }
                          }}
                          className={`border-b border-slate-200 hover:bg-slate-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#38513b]/40 ${
                            isHighlighted ? "bg-[#f6faf7] ring-1 ring-inset ring-[#38513b]/30" : ""
                          }`}
                        >
                          <td className="px-3 py-3 font-semibold text-slate-900">
                            #{item.orderDisplayId || item.orderNumber || String(item._id).slice(-8)}
                            <p className="mt-0.5 text-xs font-normal text-slate-500">
                              {formatCurrency(item.totalPrice)}
                            </p>
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadge(vm.status)}`}
                            >
                              {vm.status === "open"
                                ? "Awaiting mediation"
                                : vm.status === "mediating"
                                  ? "Mediating"
                                  : vm.status === "resolved"
                                    ? "Resolved"
                                    : vm.status || "—"}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-slate-800">{item.buyerName || "Buyer"}</td>
                          <td className="px-3 py-3 text-slate-800">{item.sellerName || "Seller"}</td>
                          <td className="px-3 py-3 whitespace-nowrap text-slate-600">
                            {formatDateTime(vm.requestedAt || vm.resolvedAt)}
                          </td>
                          <td className="max-w-[220px] px-3 py-3 text-slate-600">
                            <p className="line-clamp-2">{vm.buyerNote || "—"}</p>
                          </td>
                          <td className="max-w-[220px] px-3 py-3 text-slate-600">
                            {vm.status === "resolved" ? (
                              <>
                                <p className="text-xs font-semibold text-[#38513b]">
                                  {outcomeLabel(vm.outcomeType)}
                                </p>
                                <p className="line-clamp-2">{vm.outcomeSummary || "—"}</p>
                              </>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openCase(item._id);
                              }}
                              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-[#38513b] hover:bg-[#38513b]/5"
                            >
                              {vm.status === "open" || vm.status === "mediating" ? "Review & respond" : "View order"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardVaMediation;
